/* ==========================================================================
   Mivo Jewels — behaviour
   No dependencies. Bootstrap and Swiper are still loaded by the Jhango base
   for the cart drawer and modals, but nothing here relies on them.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_MODE = 'mivo:mode';
  var STORAGE_WISHLIST = 'mivo:wishlist';

  /* --- Mode -------------------------------------------------------------
     The visible mode always comes from the template being rendered. We only
     persist it so that pages which are not mode-specific (product, cart,
     collection) can echo the shopper's last choice in their accent colour.
     ------------------------------------------------------------------- */

  function readStored(key, fallback) {
    try {
      return window.localStorage.getItem(key) || fallback;
    } catch (e) {
      return fallback;
    }
  }

  function write(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* Private mode or storage disabled: mode simply will not persist. */
    }
  }

  function initMode() {
    var root = document.documentElement;
    var declared = root.getAttribute('data-mivo-mode');

    // The homepage declares a mode purely for its palette. It is not a
    // storefront, so it is neither remembered as the shopper's choice nor
    // reflected in the store switcher -- there the Home card is current.
    if (root.hasAttribute('data-mivo-mode-fixed')) {
      syncStoreCards('home');
      return;
    }

    if (declared) {
      // This page declares its own mode, so it is the source of truth.
      write(STORAGE_MODE, declared);
    } else {
      // Silver is the default storefront, so it is also the fallback for a
      // shopper who has not chosen one yet.
      root.setAttribute('data-mivo-mode', readStored(STORAGE_MODE, 'silver'));
    }

    syncStoreCards(root.getAttribute('data-mivo-mode'));
  }

  /* The store cards in the bottom navigation's sheet are ticked by
     [data-mivo-mode] in CSS. aria-current is set here so the current store
     is announced as well as shown; doing it in Liquid would mean guessing
     the mode on pages that do not declare one. */
  function syncStoreCards(store) {
    document.querySelectorAll('[data-mivo-store]').forEach(function (card) {
      if (card.getAttribute('data-mivo-store') === store) {
        card.setAttribute('aria-current', 'true');
      } else {
        card.removeAttribute('aria-current');
      }
    });
  }

  /* --- Wishlist ---------------------------------------------------------
     Stores handles locally and reflects them as aria-pressed. This is a
     presentational stub: swapping in a real wishlist app means replacing
     toggleWishlist, not touching any section markup.
     ------------------------------------------------------------------- */

  function getWishlist() {
    try {
      return JSON.parse(readStored(STORAGE_WISHLIST, '[]')) || [];
    } catch (e) {
      return [];
    }
  }

  function toggleWishlist(handle, button) {
    var list = getWishlist();
    var index = list.indexOf(handle);

    if (index === -1) {
      list.push(handle);
    } else {
      list.splice(index, 1);
    }

    write(STORAGE_WISHLIST, JSON.stringify(list));
    button.setAttribute('aria-pressed', String(index === -1));
  }

  function initWishlist() {
    var saved = getWishlist();

    document.querySelectorAll('[data-mivo-wish]').forEach(function (button) {
      var handle = button.getAttribute('data-mivo-wish');

      if (saved.indexOf(handle) !== -1) {
        button.setAttribute('aria-pressed', 'true');
      }

      button.addEventListener('click', function (event) {
        // Cards are wrapped in a link; don't navigate when hitting the heart.
        event.preventDefault();
        event.stopPropagation();
        toggleWishlist(handle, button);
      });
    });
  }

  /* --- Video cards ------------------------------------------------------
     Cards render a poster image until played, so six autoplaying videos
     never load on first paint.
     ------------------------------------------------------------------- */

  function initVideos() {
    document.querySelectorAll('[data-mivo-video]').forEach(function (card) {
      var button = card.querySelector('.mivo-video__play');
      if (!button) return;

      button.addEventListener('click', function () {
        var src = card.getAttribute('data-mivo-video');
        if (!src) return;

        var video = document.createElement('video');
        video.src = src;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');

        var poster = card.querySelector('img');
        if (poster) poster.remove();
        button.remove();
        card.appendChild(video);
      });
    });
  }

  /* --- Navigation dropdowns ---------------------------------------------
     Deliberately not Bootstrap: the Mivo components are self-contained, and
     the base theme's dropdown markup does not match this structure.
     ------------------------------------------------------------------- */

  function initDropdowns() {
    var triggers = Array.prototype.slice.call(
      document.querySelectorAll('[data-mivo-dropdown]')
    );
    if (!triggers.length) return;

    function panelFor(trigger) {
      return document.getElementById(trigger.getAttribute('aria-controls'));
    }

    function close(trigger) {
      var panel = panelFor(trigger);
      trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    }

    function closeAll(except) {
      triggers.forEach(function (t) {
        if (t !== except) close(t);
      });
    }

    function open(trigger) {
      var panel = panelFor(trigger);
      closeAll(trigger);
      trigger.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          close(trigger);
        } else {
          open(trigger);
        }
      });

      // Keep the panel open while focus is inside it.
      var panel = panelFor(trigger);
      if (panel) {
        panel.addEventListener('click', function (event) {
          event.stopPropagation();
        });
      }
    });

    document.addEventListener('click', function () {
      closeAll(null);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;

      // Return focus to the trigger whose panel we just closed, so keyboard
      // users are not dropped back at the top of the document.
      var openTrigger = triggers.filter(function (t) {
        return t.getAttribute('aria-expanded') === 'true';
      })[0];

      closeAll(null);
      if (openTrigger) openTrigger.focus();
    });
  }

  /* --- Mobile menu drawer -----------------------------------------------
     The burger lives in the header section and the drawer in the navigation
     section, so they are wired by id rather than by DOM proximity. If the
     navigation section is not in the header group there is no drawer, and
     the burger hides itself rather than sitting there doing nothing -- which
     is what it did while it pointed at the base theme's #offcanvas-menu, an
     element this header group never renders.
     ------------------------------------------------------------------- */

  function initMobileMenu() {
    if (!document.querySelector('[data-mivo-menu-toggle]')) return;

    // Everything is delegated off the document and the drawer is looked up
    // per event. Binding to the nodes instead would break in the theme
    // editor, where editing the navigation section swaps the drawer for a
    // fresh copy and leaves the burger holding a detached element.
    function drawerEl() {
      return document.querySelector('[data-mivo-menu]');
    }

    function toggleEls() {
      return Array.prototype.slice.call(
        document.querySelectorAll('[data-mivo-menu-toggle]')
      );
    }

    var closeTimer = null;
    var lastToggle = null;

    function open(drawer, toggle) {
      lastToggle = toggle;
      clearTimeout(closeTimer);
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';

      // One frame between unhiding and the class, or the transition has no
      // starting position to animate from.
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
      });

      toggleEls().forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });

      var closeButton = drawer.querySelector('.mivo-menu__close');
      if (closeButton) closeButton.focus();
    }

    function close(returnFocus) {
      var drawer = drawerEl();
      if (!drawer || drawer.hidden) return;

      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      toggleEls().forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        drawer.hidden = true;
      }, 320);

      // Focus goes back to the burger when the shopper dismissed the drawer,
      // but not when they followed a link out of it.
      if (returnFocus && lastToggle) lastToggle.focus();
    }

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var toggle = target.closest('[data-mivo-menu-toggle]');
      if (toggle) {
        var drawer = drawerEl();

        // No navigation section in this header group means no drawer, so the
        // burger removes itself rather than sitting there doing nothing.
        if (!drawer) {
          toggleEls().forEach(function (t) { t.hidden = true; });
          return;
        }

        if (drawer.hidden) {
          open(drawer, toggle);
        } else {
          close(true);
        }
        return;
      }

      if (target.closest('[data-mivo-menu-close]')) {
        close(true);
        return;
      }

      var accordion = target.closest('[data-mivo-menu-accordion]');
      if (accordion) {
        var sub = document.getElementById(accordion.getAttribute('aria-controls'));
        var isOpen = accordion.getAttribute('aria-expanded') === 'true';
        accordion.setAttribute('aria-expanded', String(!isOpen));
        if (sub) sub.hidden = isOpen;
        return;
      }

      // Following a link closes the drawer, so coming back with the browser's
      // back button does not land on an open menu.
      if (target.closest('.mivo-menu a[href]')) close(false);
    });

    document.addEventListener('keydown', function (event) {
      var drawer = drawerEl();
      if (!drawer || drawer.hidden) return;

      if (event.key === 'Escape') {
        close(true);
        return;
      }

      // Keep Tab inside the panel while it is modal.
      if (event.key !== 'Tab') return;

      var panel = drawer.querySelector('.mivo-menu__panel');
      if (!panel) return;

      var focusable = Array.prototype.slice.call(
        panel.querySelectorAll('a[href], button:not([disabled])')
      ).filter(function (el) { return el.offsetParent !== null; });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Resizing up to the desktop layout brings back the full navigation row,
    // so an open drawer would be a leftover from a viewport that no longer
    // exists.
    window.addEventListener('resize', function () {
      if (window.innerWidth > 900) close(false);
    });

    // The burger is only offered when there is something for it to open, and
    // that answer can change under the merchant in the theme editor.
    function syncToggles() {
      var present = !!drawerEl();
      toggleEls().forEach(function (t) { t.hidden = !present; });
    }

    syncToggles();
    document.addEventListener('shopify:section:load', syncToggles);
  }

  /* --- Sticky navigation row ---------------------------------------------
     On desktop only the navigation row stays pinned. The header group is
     position: sticky with a negative top equal to the strip plus the header
     bar (see mivo.css), so the bar parks above the viewport; this measures
     the bar into --mivo-header-h and flags the group with .is-condensed
     once the bar is out of view, which is what pops the logo and compact
     actions into the row. IntersectionObserver rather than a scroll
     listener: the browser tells us when the bar crosses the edge and
     nothing runs on every frame.

     The logo slot in the row is filled with a copy of the header's own
     logo markup so it is uploaded once. Everything is re-run on
     shopify:section:load because the editor re-renders either section
     without reloading the page.
     ------------------------------------------------------------------- */
  function initStickyNav() {
    var group = document.getElementById('header-group');
    if (!group || !('IntersectionObserver' in window)) return;

    var root = document.documentElement;
    var observer = null;
    var sizer = null;

    function headerEl() { return group.querySelector('.mivo-header'); }

    function measure() {
      var header = headerEl();
      root.style.setProperty('--mivo-header-h', (header ? header.offsetHeight : 0) + 'px');
    }

    function fillLogo() {
      var slot = group.querySelector('[data-mivo-nav-logo]');
      var source = group.querySelector('.mivo-header__logo');
      if (!slot) return;
      slot.innerHTML = source ? source.innerHTML : '';
    }

    function watch() {
      if (observer) observer.disconnect();
      if (sizer) sizer.disconnect();

      var header = headerEl();
      if (!header) {
        group.classList.remove('is-condensed');
        measure();
        return;
      }

      // The bar parks with its bottom edge exactly on the viewport edge,
      // which the observer still counts as touching; the 1px inset makes
      // "fully scrolled away" mean what it says.
      observer = new IntersectionObserver(function (entries) {
        group.classList.toggle('is-condensed', !entries[0].isIntersecting);
      }, { threshold: 0, rootMargin: '-1px 0px 0px 0px' });
      observer.observe(header);

      if ('ResizeObserver' in window) {
        sizer = new ResizeObserver(measure);
        sizer.observe(header);
      }
      measure();
    }

    function setup() {
      fillLogo();
      watch();
    }

    setup();
    window.addEventListener('resize', measure);
    document.addEventListener('shopify:section:load', setup);
  }

  /* --- Store switcher sheet ---------------------------------------------
     The bottom navigation's Switch Stores button and the sheet it opens
     live in the same section, but everything is still delegated off the
     document and looked up per event, for the same reason as the drawer:
     the theme editor swaps the section for a fresh copy on every edit.
     ------------------------------------------------------------------- */

  function initStoreSheet() {
    if (!document.querySelector('[data-mivo-stores-toggle]')) return;

    function sheetEl() {
      return document.querySelector('[data-mivo-stores]');
    }

    function toggleEls() {
      return Array.prototype.slice.call(
        document.querySelectorAll('[data-mivo-stores-toggle]')
      );
    }

    var closeTimer = null;
    var lastToggle = null;

    function open(sheet, toggle) {
      lastToggle = toggle;
      clearTimeout(closeTimer);
      sheet.hidden = false;
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(function () {
        sheet.classList.add('is-open');
      });

      toggleEls().forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });

      var closeButton = sheet.querySelector('.mivo-stores__close');
      if (closeButton) closeButton.focus();
    }

    function close(returnFocus) {
      var sheet = sheetEl();
      if (!sheet || sheet.hidden) return;

      sheet.classList.remove('is-open');
      document.body.style.overflow = '';
      toggleEls().forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        sheet.hidden = true;
      }, 320);

      if (returnFocus && lastToggle) lastToggle.focus();
    }

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var toggle = target.closest('[data-mivo-stores-toggle]');
      if (toggle) {
        var sheet = sheetEl();
        if (!sheet) return;

        if (sheet.hidden) {
          open(sheet, toggle);
        } else {
          close(true);
        }
        return;
      }

      if (target.closest('[data-mivo-stores-close]')) {
        close(true);
        return;
      }

      // Following a store link closes the sheet, so the back button does
      // not land on an open sheet.
      if (target.closest('.mivo-stores a[href]')) close(false);
    });

    document.addEventListener('keydown', function (event) {
      var sheet = sheetEl();
      if (!sheet || sheet.hidden) return;

      if (event.key === 'Escape') {
        close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      var panel = sheet.querySelector('.mivo-stores__panel');
      if (!panel) return;

      var focusable = Array.prototype.slice.call(
        panel.querySelectorAll('a[href], button:not([disabled])')
      ).filter(function (el) { return el.offsetParent !== null; });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // The bar that opened the sheet is gone above 900px, so the sheet goes
    // with it.
    window.addEventListener('resize', function () {
      if (window.innerWidth > 900) close(false);
    });

    // The section is re-rendered by the theme editor with fresh cards that
    // have no aria-current yet.
    document.addEventListener('shopify:section:load', function () {
      var root = document.documentElement;
      syncStoreCards(
        root.hasAttribute('data-mivo-mode-fixed') ? 'home' : root.getAttribute('data-mivo-mode')
      );
    });
  }

  /* --- Rotating search placeholder --------------------------------------
     Progressive enhancement only: the input already carries a usable
     placeholder rendered by Liquid. Pauses while the field is in use.
     ------------------------------------------------------------------- */

  function initSearchPlaceholder() {
    var input = document.querySelector('[data-mivo-placeholder]');
    if (!input) return;

    var terms = (input.getAttribute('data-mivo-placeholder') || '')
      .split(',')
      .map(function (t) { return t.trim(); })
      .filter(Boolean);

    if (terms.length < 2) return;

    var prefix = input.getAttribute('data-mivo-placeholder-prefix') || 'Search';
    var index = 0;
    var paused = false;

    input.addEventListener('focus', function () { paused = true; });
    input.addEventListener('blur', function () { paused = !!input.value; });

    var reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    setInterval(function () {
      if (paused) return;
      index = (index + 1) % terms.length;
      input.setAttribute('placeholder', prefix + ' "' + terms[index] + '"');
    }, 2600);
  }

  /* --- Product carousels ------------------------------------------------
     The track is a CSS scroll-snap rail, so swiping, trackpad scrolling and
     keyboard scrolling all work with none of this. What is added here is a
     pair of arrows that page through it, plus the honesty to hide them when
     there is nothing to page to: four products in a four-column carousel do
     not overflow, and arrows that cannot move are worse than no arrows.
     ------------------------------------------------------------------- */

  function initCarousels() {
    document.querySelectorAll('[data-mivo-carousel]').forEach(function (root) {
      // The theme editor re-runs this on every section load, and a section
      // that was already wired would otherwise collect a second set of
      // listeners and scroll twice per click.
      if (root.hasAttribute('data-mivo-carousel-ready')) return;
      root.setAttribute('data-mivo-carousel-ready', '');

      var track = root.querySelector('[data-mivo-carousel-track]');
      var prev = root.querySelector('[data-mivo-carousel-prev]');
      var next = root.querySelector('[data-mivo-carousel-next]');
      if (!track || !prev || !next) return;

      var GAP = 24;

      // Page by as many whole columns as are on screen. The exact figure
      // does not have to be perfect: scroll snapping is mandatory on this
      // track, so the browser settles onto the nearest card edge afterwards.
      function page() {
        var slide = track.querySelector('.mivo-carousel__slide');
        if (!slide) return track.clientWidth;

        var step = slide.offsetWidth + GAP;
        var columns = Math.max(1, Math.floor(track.clientWidth / step));
        return step * columns;
      }

      function sync() {
        // Sub-pixel column widths mean neither end of the track lands on a
        // round number, so both edges get a couple of pixels of slack.
        var max = track.scrollWidth - track.clientWidth;
        var overflows = max > 2;

        prev.hidden = !overflows;
        next.hidden = !overflows;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
      }

      prev.addEventListener('click', function () {
        track.scrollBy({ left: -page(), behavior: 'smooth' });
      });

      next.addEventListener('click', function () {
        track.scrollBy({ left: page(), behavior: 'smooth' });
      });

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);

      sync();

      // Lazy-loaded card images have no height until they arrive, so the
      // first measurement can read a track that does not yet overflow.
      window.addEventListener('load', sync);
    });
  }

  // Stats band: each number counts up from zero the first time it scrolls
  // into view. The final value is already in the markup, so without JS, or
  // with reduced motion, the number is simply shown.
  function initCounters() {
    var counters = document.querySelectorAll('[data-mivo-counter]:not([data-mivo-counter-done])');
    if (!counters.length) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      counters.forEach(function (el) { el.setAttribute('data-mivo-counter-done', ''); });
      return;
    }

    function run(el) {
      var target = parseInt(el.getAttribute('data-mivo-counter'), 10);
      if (isNaN(target)) return;
      var duration = 1400;
      var start = null;
      function frame(now) {
        if (start === null) start = now;
        var t = Math.min((now - start) / duration, 1);
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = Math.round(target * eased).toLocaleString();
        if (t < 1) requestAnimationFrame(frame);
      }
      el.textContent = '0';
      requestAnimationFrame(frame);
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        observer.unobserve(el);
        el.setAttribute('data-mivo-counter-done', '');
        run(el);
      });
    }, { threshold: 0.4 });

    counters.forEach(function (el) { observer.observe(el); });
  }

  function init() {
    initMode();
    initWishlist();
    initVideos();
    initDropdowns();
    initMobileMenu();
    initStickyNav();
    initStoreSheet();
    initSearchPlaceholder();
    initCarousels();
    initCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Switching a rail to the carousel layout in the theme editor re-renders
  // the section without reloading the page, so the new track needs wiring.
  document.addEventListener('shopify:section:load', initCarousels);
  document.addEventListener('shopify:section:load', initCounters);
})();
